import React, {useMemo, useState} from "react";
import {Table} from "../table/Table";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, IconButton, Stack, Typography} from "@mui/material";
import { useNavigate, useParams, Link } from "react-router";
import {enqueueSnackbar} from "notistack";
import {DefaultError} from "@tanstack/query-core";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import {TextInput} from "../form/TextInput";
import {Highlighter} from "../common/Highlighter";
import {sanitizeFilterObject} from "../../service/http.service";
import {TableHeaderProps} from "../table/TableTypes";
import {deleteTicket, loadTicketsPage, SupportTicketFilter} from "../../service/ticket.service";
import {SupportTicket} from "../../types/SupportTicket";
import {Delete} from "../common/Delete";
import { SupportViewForm } from "./SupportViewForm";
import { formatTime } from "../../utils/DateUtils";
import { SupportTicketForm } from "./SupportTicketForm";

export function SupportTicketsTable ({goBack}:{goBack:() => void}){
    const {id: idToEditFromUrl} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [idToEditFromTable, setIdToEditFromTable] = useState<string>();

    const idToEdit = useMemo(() => {
        return idToEditFromUrl || idToEditFromTable
    }, [idToEditFromUrl, idToEditFromTable])

    const deleteMutation = useMutation<void, DefaultError, string>({
        mutationFn: (id) => deleteTicket(id).then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["tickets"]}).catch(e => {
                enqueueSnackbar({message: "Failed to clean cache after delete ticket: " + e.message, variant: "error"});
            });
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to delete ticket: " + error.message, variant: "error"});
        }
    });

    const [filter, setFilter] = React.useState<SupportTicketFilter>({
        page: 0,
        size: 25,
        sortKey: "createdAt",
        sortOrder: "desc",
        title: "",
        content: "",
    });

    const {data, isFetching, error} = useQuery({
        queryKey: ["tickets", filter],
        refetchOnWindowFocus: false,
        queryFn: () => loadTicketsPage(ticketFilter(filter)).then(r => r.data),
        placeholderData: keepPreviousData,
    });

    const headers: TableHeaderProps<SupportTicket>[] = useMemo(() => [
        {
            key: "title",
            title: "Title",
            render: (row) =>
                <Highlighter onClick={() => setIdToEditFromTable(row.id)} text={row.title} search={filter.title}/>
        },
        {
            key: "content",
            title: "Content",
            render: (row) => <Highlighter
                onClick={() => setIdToEditFromTable(row.id)}
                text={row.content}
                search={filter.content}/>
        },
        {
            key: 'status',
            title: "Status",
            render: (row) => row.status
        },
        {
            key: "entityType",
            title: "Link",
            render: (row) => {
                return row.entityId && <Stack>
                    <div>
                        <Button component={Link}
                                to={"/prop/" + row.entityId}
                                target={"_blank"}>Open</Button>
                    </div>
                </Stack>
            }
        },
        {
            key: "createdAt",
            title: "Created At",
            render: (row) => formatTime(row.createdAt)
        },
        {
            key: "controlPanel",
            disableSort: true,
            title: <Stack flexDirection={"row-reverse"}>

            </Stack>,
            render: (row) => <Stack flexDirection="row" gap="5px" justifyContent="space-evenly">
                <IconButton onClick={() => setIdToEditFromTable(row.id)}>
                    <EditNoteOutlinedIcon/>
                </IconButton>
                <Delete onConfirm={() => deleteMutation.mutate(row.id)}/>
            </Stack>
        }
    ], [deleteMutation, filter.title, filter.content, navigate]);

    function ticketFilter(filter: SupportTicketFilter) {
        const out = {...filter};
        if (out.title) {
            out.title = "%" + out.title + "%";
        }
        if (out.content) {
            out.content = "%" + out.content + "%";
        }
        return sanitizeFilterObject(out);
    }

    if (idToEdit) {
        return <SupportViewForm ticketId={idToEdit}
                                onClose={() => {
                                    goBack();
                                    setIdToEditFromTable(undefined);
                                }}/>;
    }

    return <>
        <Typography sx={{maxWidth: '1200px', margin: '0 0 15px'}} variant={"h4"}>
            Create New Ticket
        </Typography>
        <SupportTicketForm
            margin={"0 0 15px"}
            defaultTitle={""}
            entityId={""}/>
        {error && <Alert color="error">{error?.name + ": " + error?.message}</Alert>}
        {false && <Stack flexDirection="column" className="mb-15">
            <Stack flexDirection="row" gap="15px">
                <TextInput label="Title" value={filter.title || ""} onChange={(e) => setFilter({
                    ...filter, page: 0, title: e.target.value
                })}/>
                <TextInput label="Content" value={filter.content || ""} onChange={(e) => setFilter({
                    ...filter, page: 0, content: e.target.value
                })}/>
            </Stack>
        </Stack>}
        <Table data={data?.content || []}
               sort={{
                   key: filter.sortKey,
                   order: filter.sortOrder,
                   onChange: (key, order) => setFilter({...filter, page: 0, sortKey: key, sortOrder: order})
               }}
               isLoading={isFetching}
               total={data?.totalElements || 0}
               page={filter.page}
               size={filter.size}
               headers={headers}
               pageChange={(page, size) => setFilter({...filter, page, size})}/>

    </>;
};